// src/frontend/src/pages/CreateCustomScenario.tsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout/Layout';
import { customScenarioService } from '../api/simulationApi';
import { 
  StakeholderType, 
  ChallengeType, 
  Difficulty, 
  LearningObjective 
} from '../types/simulation';

const CreateCustomScenario: React.FC = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    industry: '',
    projectType: '',
    customContext: '',
    stakeholderTypes: [] as string[],
    challengeTypes: [] as string[],
    learningObjectives: [] as string[],
    difficulty: Difficulty.MEDIUM,
    isPublic: false
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  const handleMultiSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name } = e.target;
    const options = Array.from(e.target.selectedOptions, option => option.value);
    setFormData(prev => ({ ...prev, [name]: options }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    // Validation
    if (!formData.title || !formData.description || !formData.industry || 
        !formData.projectType || !formData.customContext || 
        formData.stakeholderTypes.length === 0 || 
        formData.challengeTypes.length === 0 || 
        formData.learningObjectives.length === 0) {
      setError('Please fill all required fields');
      return;
    }
    
    try {
      setIsSubmitting(true);
      const response = await customScenarioService.createCustomScenario(formData);
      
      // Navigate to the custom scenario page
      navigate('/custom-scenarios');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create custom scenario');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper to format enum values for display
  const formatEnumValue = (value: string): string => {
    return value
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-md p-6">
        <h1 className="text-2xl font-bold mb-6">Create Custom Scenario Template</h1>
        
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="title" className="block text-gray-700 font-medium mb-2">
              Template Title *
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring focus:border-blue-300"
              required
            />
          </div>
          
          <div className="mb-4">
            <label htmlFor="description" className="block text-gray-700 font-medium mb-2">
              Template Description *
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={2}
              className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring focus:border-blue-300"
              required
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label htmlFor="industry" className="block text-gray-700 font-medium mb-2">
                Industry *
              </label>
              <input
                type="text"
                id="industry"
                name="industry"
                value={formData.industry}
                onChange={handleChange}
                placeholder="e.g. Healthcare, Construction, Technology"
                className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring focus:border-blue-300"
                required
              />
            </div>
            
            <div>
              <label htmlFor="projectType" className="block text-gray-700 font-medium mb-2">
                Project Type *
              </label>
              <input
                type="text"
                id="projectType"
                name="projectType"
                value={formData.projectType}
                onChange={handleChange}
                placeholder="e.g. Software Development, Building Construction, Product Launch"
                className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring focus:border-blue-300"
                required
              />
            </div>
          </div>
          
          <div className="mb-4">
            <label htmlFor="customContext" className="block text-gray-700 font-medium mb-2">
              Custom Context *
            </label>
            <textarea
              id="customContext"
              name="customContext"
              value={formData.customContext}
              onChange={handleChange}
              rows={5}
              placeholder="Describe the specific project context, challenges, and any other details that would help create a realistic simulation."
              className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring focus:border-blue-300"
              required
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label htmlFor="stakeholderTypes" className="block text-gray-700 font-medium mb-2">
                Stakeholder Types *
              </label>
              <select
                id="stakeholderTypes"
                name="stakeholderTypes"
                multiple
                value={formData.stakeholderTypes}
                onChange={handleMultiSelectChange}
                className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring focus:border-blue-300"
                required
                size={6}
              >
                {Object.values(StakeholderType).map((type) => (
                  <option key={type} value={type}>
                    {formatEnumValue(type)}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">Hold Ctrl/Cmd to select multiple</p>
            </div>
            
            <div>
              <label htmlFor="challengeTypes" className="block text-gray-700 font-medium mb-2">
                Challenge Types *
              </label>
              <select
                id="challengeTypes"
                name="challengeTypes"
                multiple
                value={formData.challengeTypes}
                onChange={handleMultiSelectChange}
                className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring focus:border-blue-300"
                required
                size={6}
              >
                {Object.values(ChallengeType).map((type) => (
                  <option key={type} value={type}>
                    {formatEnumValue(type)}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">Hold Ctrl/Cmd to select multiple</p>
            </div>
            
            <div>
              <label htmlFor="learningObjectives" className="block text-gray-700 font-medium mb-2">
                Learning Objectives *
              </label>
              <select
                id="learningObjectives"
                name="learningObjectives"
                multiple
                value={formData.learningObjectives}
                onChange={handleMultiSelectChange}
                className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring focus:border-blue-300"
                required
                size={6}
              >
                {Object.values(LearningObjective).map((objective) => (
                  <option key={objective} value={objective}>
                    {formatEnumValue(objective)}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">Hold Ctrl/Cmd to select multiple</p>
            </div>
          </div>
          
          <div className="mb-6">
            <label htmlFor="difficulty" className="block text-gray-700 font-medium mb-2">
              Difficulty Level
            </label>
            <select
              id="difficulty"
              name="difficulty"
              value={formData.difficulty}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring focus:border-blue-300"
            >
              {Object.values(Difficulty).map((level) => (
                <option key={level} value={level}>
                  {formatEnumValue(level)}
                </option>
              ))}
            </select>
          </div>
          
          <div className="mb-6">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="isPublic"
                name="isPublic"
                checked={formData.isPublic}
                onChange={handleCheckboxChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="isPublic" className="ml-2 block text-gray-700">
                Make this template public for other users
              </label>
            </div>
          </div>
          
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => navigate('/coaching-sessions')}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 mr-2 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className={`bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring ${
                isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isSubmitting ? 'Creating...' : 'Create Template'}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default CreateCustomScenario;